import { create } from 'zustand';
import { Pagination } from '../types/Pagination';
import { useCases } from '../../infrastructure/di/container';
import { ChannelEntity } from '../../domain/entities/Channel';
import { ChannelFilters, DEFAULT_CHANNEL_FILTERS } from '../../shared/types/ChannelFilters';

interface ChannelsState {
  channels: ChannelEntity[];
  pagination: Pagination | null;
  filters: ChannelFilters;
  isLoading: boolean;
  error?: string;
  setFilters: (filters: ChannelFilters) => void;
  list: (page?: number, perPage?: number, filters?: ChannelFilters, append?: boolean) => Promise<void>;
  listAll: (filters?: ChannelFilters) => Promise<ChannelEntity[]>;
  countFor: (filters: ChannelFilters) => Promise<number>;
  create: (input: { link: string; title?: string; type: string; isPrivate: boolean; minimalSendingInterval: number; sendingStartTime: string; sendingEndTime: string }) => Promise<ChannelEntity | undefined>;
  update: (uuid: string, input: { link?: string; title?: string; type?: string; isPrivate?: boolean; minimalSendingInterval?: number; sendingStartTime?: string; sendingEndTime?: string }) => Promise<ChannelEntity | undefined>;
  remove: (uuid: string) => Promise<boolean>;
  reset: () => void;
}

export const useChannelsStore = create<ChannelsState>((set, get) => ({
  channels: [],
  pagination: null,
  filters: DEFAULT_CHANNEL_FILTERS,
  isLoading: false,
  setFilters(filters) { set({ filters }); },
  async list(page = 1, perPage = 10, filters, append = false) {
    const effectiveFilters = filters ?? get().filters;
    set({ isLoading: true, error: undefined, filters: effectiveFilters });
    try {
      const res = await useCases.listChannels().execute({ page, perPage, ...effectiveFilters });
      set({
        channels: append ? [...get().channels, ...res.items] : res.items,
        pagination: { page: res.page, perPage: res.perPage, total: res.total },
        isLoading: false,
      });
    } catch (e: any) { set({ isLoading: false, error: e?.message || 'Failed to load channels' }); }
  },
  async listAll(filters) {
    return useCases.listAllChannels().execute(filters ?? get().filters);
  },
  async countFor(filters) {
    return useCases.countChannels().execute(filters);
  },
  async create(input) {
    set({ isLoading: true, error: undefined });
    try {
      const entity = await useCases.createChannel().execute(input);
      set({ channels: [entity, ...get().channels], isLoading: false });
      return entity;
    } catch(e:any){ set({ isLoading:false, error:e?.message||'Failed to create channel' }); }
  },
  async update(uuid, input) {
    set({ isLoading: true, error: undefined });
    try {
      const entity = await useCases.updateChannel().execute(uuid, input);
      set({ channels: get().channels.map(c=>c.uuid===uuid?entity:c), isLoading:false });
      return entity;
    } catch(e:any){ set({ isLoading:false, error:e?.message||'Failed to update channel' }); }
  },
  async remove(uuid) {
    set({ isLoading: true, error: undefined });
    try { await useCases.deleteChannel().execute(uuid); set({ channels: get().channels.filter(c=>c.uuid!==uuid), isLoading:false }); return true; } catch(e:any){ set({ isLoading:false, error:e?.message||'Failed to delete channel' }); return false; }
  },
  reset() { set({ channels: [], pagination: null, filters: DEFAULT_CHANNEL_FILTERS }); }
}));
